import { useMemo } from 'react';

import ReadOnlyMap from '@components/map/readOnlyMap';
import LabelValue from '@components/label-value/LabelValue';

import { calculateGeometryExtent } from '@utils/helpers';
import type { HelpInquirySummary } from '@api/services/help/types';

import './helpRequestSummary.scss';

type HelpRequestSummaryProps = {
  summary: HelpInquirySummary;
};

const getRequesterEmailHref = (email?: string | null, projectName?: string | null) => {
  if (!email) return undefined;

  const subject = encodeURIComponent(`RRS Help Inquiry Follow-up: ${projectName ?? 'Request'}`);

  return `mailto:${email}?subject=${subject}`;
};

const HelpRequestSummary = ({ summary }: HelpRequestSummaryProps) => {
  const location = summary.location;

  const geometry = useMemo(() => {
    if (location?.geometry) {
      return location.geometry;
    }

    if (
      location?.latitude === null ||
      location?.latitude === undefined ||
      location?.longitude === null ||
      location?.longitude === undefined
    ) {
      return null;
    }

    return {
      type: 'Point',
      coordinates: [location.longitude, location.latitude],
    };
  }, [location?.geometry, location?.latitude, location?.longitude]);

  const requesterEmailHref = getRequesterEmailHref(summary.email, summary.projectName);

  const requesterEmailValue = requesterEmailHref ? <a href={requesterEmailHref}>{summary.email}</a> : summary.email;

  return (
    <div className='info-container'>
      <div>
        <section className='info-section flex-item'>
          <h3>Details</h3>

          <div className='help-request-summary__details-grid'>
            <div className='help-request-summary__details-group'>
              <LabelValue label='Project Name' value={summary?.projectName} />
              <LabelValue label='Request Type' value={summary?.requestType} />
              <LabelValue label='Date Submitted' value={summary?.createdDate} />
            </div>

            <div className='help-request-summary__details-group'>
              <LabelValue label='Inquiry Categories' value={summary?.helpCategories?.join(', ')} />
              <LabelValue label='Assisting Regulator' value={summary?.regulatorName} />
              <LabelValue label='Inquiry Status' value={summary?.status} />
            </div>

            <div className='help-request-summary__details-group'>
              <LabelValue label='Requester Name' value={summary?.name} />
              <LabelValue label='Requester Email' value={requesterEmailValue} />
            </div>
          </div>

          <div className='help-request-summary__reason'>
            <div className='help-request-summary__reason-label'>Reason:</div>
            <div className='help-request-summary__reason-content'>{summary.reason ?? '-'}</div>
          </div>
        </section>
      </div>

      <section className='info-section'>
        <h3>Location</h3>
        <div className='info-flex-container'>
          <div className='info-flex-item'>
            {geometry && <ReadOnlyMap geometry={geometry} extent={calculateGeometryExtent(geometry)} height='240px' />}
          </div>

          <div className='info-flex-item'>
            <div className='info-flex-container'>
              <div className='info-flex-item'>
                <LabelValue label='City' value={location?.city} />
                <LabelValue label='State' value={location?.state} />
                <LabelValue label='Zip Code' value={location?.zip} />
              </div>
              <div className='info-flex-item'>
                <LabelValue label='District' value={location?.district} />
                <LabelValue label='Coordinates' value={`${location?.latitude}, ${location?.longitude}`} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpRequestSummary;
